<!--
  SourceElement.vue

  Displays a single external source as an icon-only square chip — the building
  block for source field displays (parallel to ContactElement / TypeElement).

  Renders the source type's branded icon in white on a rounded square filled
  with the source's brand colour (per the registry). When the source resolves
  to an external URL it becomes a link to that record in the upstream system;
  otherwise it renders as a plain (titled) span.

  Props:
    - value: A record_sources row ({ source_type, source_id, label? }) or a
      bare source type string (e.g. "pipedrive_person").
    - size: Chip size ('small', 'normal').
    - fixed: Disable the external link even when a URL resolves (default: false),
      mirroring RecordElement.
-->
<template>
  <a
    v-if="href"
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    class="source-element text-inverted"
    :class="sizeClass"
    :title="title"
    :style="{ backgroundColor: color }"
  >
    <font-awesome-icon :icon="icon" />
  </a>
  <span
    v-else
    class="source-element text-inverted"
    :class="sizeClass"
    :title="title"
    :style="{ backgroundColor: color }"
  >
    <font-awesome-icon :icon="icon" />
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue"
import {
  getSourceColor,
  getSourceExternalUrl,
  getSourceIcon,
  getSourceLabel,
} from "@/common/sourceRegistry"

interface Props {
  value: { source_type?: string; source_id?: string; label?: string } | string
  size?: string
  fixed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: "normal",
  fixed: false,
})

// The prop is named `value` to match the element-component convention; the
// payload is a record_sources row (or a bare source-type string).
const source = computed(() => props.value)

const sourceType = computed(() =>
  typeof source.value === "string" ? source.value : (source.value?.source_type ?? "")
)

const sizeClass = computed(() => (props.size === "small" ? "is-small" : null))

const icon = computed(() => getSourceIcon(sourceType.value))
const color = computed(() => getSourceColor(sourceType.value))

const title = computed(() => {
  const label = getSourceLabel(sourceType.value)
  const id = typeof source.value === "object" ? source.value?.source_id : undefined
  return id ? `${label} (${id})` : label
})

const href = computed(() => {
  if (props.fixed) return null
  if (typeof source.value !== "object" || !source.value?.source_id) return null
  return getSourceExternalUrl({
    source_type: sourceType.value,
    source_id: source.value.source_id,
  })
})
</script>

<style>
.source-element {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: var(--auger-border-hover);
  font-size: var(--auger-size-7);
  opacity: 0.8;

  &.is-small {
    width: 20px;
    height: 20px;
    border-radius: 4px;
  }
}

a.source-element:hover {
  opacity: 1;
}
</style>
